import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  type User
} from 'firebase/auth';
import { getFirebase, isFirebaseConfigured } from '@/config/firebase';
import { STORAGE_KEYS } from '@/config/constants';

let currentUser: User | null = null;
let localUid: string | null = null;
const listeners: Array<(uid: string, isGoogle: boolean) => void> = [];

function genLocalUid(): string {
  const rand = () => Math.random().toString(36).slice(2, 10);
  return `local-${Date.now().toString(36)}-${rand()}${rand()}`;
}

function ensureLocalUid(): string {
  if (localUid) return localUid;
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.localUid);
    if (stored && stored.length > 0) {
      localUid = stored;
      return localUid;
    }
  } catch {
    /* localStorage may be blocked */
  }
  const fresh = genLocalUid();
  try {
    localStorage.setItem(STORAGE_KEYS.localUid, fresh);
  } catch {
    /* ignore */
  }
  localUid = fresh;
  return localUid;
}

export const authService = {
  /**
   * Boot identity: no Firebase Auth required. Returns a stable local UID
   * persisted in localStorage. Google linking is opt-in via upgradeToGoogle().
   */
  init(): { uid: string; isAnonymous: true } {
    const uid = ensureLocalUid();
    return { uid, isAnonymous: true };
  },

  async upgradeToGoogle(): Promise<{ uid: string; displayName: string | null } | null> {
    if (!isFirebaseConfigured()) return null;
    try {
      const { auth } = getFirebase();
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      currentUser = cred.user;
      const uid = cred.user.uid;
      listeners.forEach((l) => l(uid, true));
      return { uid, displayName: cred.user.displayName };
    } catch (err) {
      console.error('Google sign-in failed', err);
      return null;
    }
  },

  async logout(): Promise<void> {
    if (!isFirebaseConfigured()) {
      currentUser = null;
      return;
    }
    try {
      const { auth } = getFirebase();
      await signOut(auth);
    } catch (err) {
      console.error('signOut failed', err);
    }
    currentUser = null;
  },

  getGoogleUser(): User | null {
    return currentUser;
  },

  getLocalUid(): string {
    return ensureLocalUid();
  },

  onChange(cb: (uid: string, isGoogle: boolean) => void): () => void {
    listeners.push(cb);
    return () => {
      const i = listeners.indexOf(cb);
      if (i >= 0) listeners.splice(i, 1);
    };
  }
};
